import { Component, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { ConfirmationDialogComponent, ConfirmationDialogType, GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  DISABLE_REASON,
  FORMS_MODE,
  ICareplan,
  IPatient,
  IPatientHistoric,
  IPatientUser,
  IRelatedPerson,
  USER_STATUS,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { EventEmitter } from "events";
import * as moment from "moment";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { HelpData } from "src/app/helpers/helpData";
import { PatientHelper } from "src/app/helpers/patientHelper";
import { RelatedHelper } from "src/app/helpers/relatedHelper";
import { IPatientDeactivation, IPatientDeactivationState, IPatientDeactivationUpdate } from "src/app/models/patient-deactivation.model";
import { AuthService } from "src/app/providers/auth.service";
import { CareplansService } from "src/app/providers/careplans.service";
import { PatientService } from "src/app/providers/patient.service";
import { RelatedService } from "src/app/providers/related.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { IdentityStatusChangeHistoricComponent } from "./identity-status-change-historic/identity-status-change-historic.component";
import { PatientDeactivationUpdateDialogComponent } from "./patient-deactivation-update-dialog/patient-deactivation-update-dialog.component";
import { PatientDisableDialogComponent } from "./patient-disable-dialog/patient-disable-dialog.component";
import { PatientHistoricDialogComponent } from "./patient-historic-dialog/patient-historic-dialog.component";
import { PatientReactivateDialogComponent, REACTIVATE_CHOICE } from "./patient-reactivate-dialog/patient-reactivate-dialog.component";

interface RelatedContactDisplay {
  name: string;
  phone?: string;
  mail?: string;
}

@Component({
  selector: "app-patient-infos",
  templateUrl: "./patient-infos.component.html",
  styleUrls: ["./patient-infos.component.scss"],
})
export class PatientInfosComponent implements OnInit, OnDestroy {
  @Output() refreshCareplan = new EventEmitter();
  @Input() set patientUserSet(pu: IPatientUser) {
    this.patientUser = pu;
    this.relateds = [];
    this.updatePatientInfoExtra();
    this.loadRelateds();
    this.loadDeactivation();
    this.handleAutoEditForm();
  }
  public patientUser: IPatientUser;
  /** Deactivation the patient is currently under, null while he is active. */
  public deactivation: IPatientDeactivationState = null;
  public relateds: IRelatedPerson[] = [];
  public importantNote = "";
  public relatedContacts: RelatedContactDisplay[] = [];
  public hasPatientInfoExtra = false;
  private currentCareplan: ICareplan;
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();
  public isAdmin = false;
  public canDeactivate = false;
  public canUpdateDeactivation = false;
  public canUpdate = false;
  public canDelete = false;
  public isServiceSelected = false;
  public country: string;
  public FRANCE: string;
  public USER_STATUS = USER_STATUS;

  constructor(
    private router: Router,
    private patientService: PatientService,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private sessionService: SessionService,
    private userService: UserService,
    public helpData: HelpData,
    private authService: AuthService,
    private careplansService: CareplansService,
    private relatedService: RelatedService,
    private responsiveDialog: ResponsiveDialogService,
    private dialog: MatDialog
  ) {
    this.setupServicesWatch();
    this.country = this.sessionService.currentCountry;
    this.FRANCE = this.sessionService.FRANCE;
  }

  ngOnInit(): void {
    this.setupIsAdmin();
    this.setupCanDeactivate();
    this.setupCanUpdateDeactivation();
    this.setupCanUpdate();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private setupServicesWatch() {
    this.isServiceSelected = this.sessionService.currentService !== null ? true : false;
    this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isServiceSelected = this.sessionService.currentService !== null ? true : false;
    });
  }

  public handleAutoEditForm(): void {
    if (this.patientUser) {
      const user = this.patientUser?.user;
      const isSameDateOrUndefined = moment(user.birthdate).isSame(moment(user.creation), "day") || !user.birthdate;
      const patientGender = this.patientUser?.patient?.gender;

      if (user?.anonymous) {
        // get anonymous patient careplan
        this.careplansService
          .list(user.caremateIdentifier)
          .pipe(first(), takeUntil(this.onDestroy$))
          .subscribe((cp) => {
            if (cp.length > 0) {
              this.currentCareplan = cp[0];
            }
            if (isSameDateOrUndefined && !patientGender) {
              this.initializeAnonymousPatient();
            }
          });
      }
    }
  }

  public initializeAnonymousPatient(): void {
    this.router.navigate(["patientRegistration", "careplan"], {
      state: {
        directToCareplan: true,
        patientUser: this.patientUser,
        careplan: this.currentCareplan ? this.currentCareplan : undefined,
        includeBirthdateAndGenderForm: true,
      },
    });
  }

  public goToPatients(): void {
    const historyPatientUrl = this.sessionService.patientPageFrom;
    if (historyPatientUrl) {
      this.sessionService.patientPageFrom = "";
      this.router.navigate([historyPatientUrl]);
    } else {
      this.router.navigate(["/"]);
    }
  }

  public editPatient(): void {
    const b64Id = Base64Helper.utf8_to_b64(this.patientUser.user.caremateIdentifier);
    this.router.navigate(["patientEdition", b64Id], {
      state: { mode: FORMS_MODE.UPDATE },
    });
  }

  public getMail(patient: IPatient): string | null {
    return this.patientService.getMail(patient);
  }

  public getPhone(patient: IPatient): string | null {
    return this.patientService.getPhone(patient);
  }

  public getBirthdate(patient: IPatient): string {
    return this.patientService.getBirthdate(patient);
  }

  public getId(patient: IPatient): string | null {
    return this.patientService.getInternalId(patient);
  }

  private loadRelateds(): void {
    this.relatedService
      .getPatientRelateds(this.patientUser?.user?.caremateIdentifier)
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((relateds) => {
        this.relateds = relateds ?? [];
        this.updatePatientInfoExtra();
      });
  }

  private updatePatientInfoExtra(): void {
    this.importantNote = (this.patientUser?.patient?.note ?? "").trim();
    this.relatedContacts = this.relateds
      .filter((related) => related.active !== false)
      .map((related) => ({
        name: [related?.name?.given?.join(" "), related?.name?.family?.join(" ")].filter(Boolean).join(" ").trim(),
        phone: RelatedHelper.getRelatedPhone(related),
        mail: RelatedHelper.getRelatedMail(related),
      }))
      .filter((related) => related.phone || related.mail);
    this.hasPatientInfoExtra = Boolean(this.importantNote || this.relatedContacts.length);
  }

  /** A permanently deactivated patient is closed for good: his file cannot be edited anymore. */
  public get isPermanentlyDisabled(): boolean {
    return this.patientUser?.patient?.active === false && this.deactivation?.permanent === true;
  }

  /** Only a deactivation known to be a temporary one can still be changed. */
  public get isTemporarilyDisabled(): boolean {
    return this.patientUser?.patient?.active === false && this.deactivation?.permanent === false;
  }

  /**
   * What the patient is deactivated for: the free text typed for "other", the sub status of the
   * deactivation he is under, or, for a patient deactivated before sub statuses existed, whatever
   * reason was left on his file.
   */
  public get deactivationReason(): string {
    if (this.deactivation?.subStatus === DISABLE_REASON.OTHER) {
      return this.deactivation.reasonDetail ?? this.deactivation.subStatus;
    }
    return this.deactivation?.subStatus ?? this.patientUser?.patient?.disabledReason ?? "";
  }

  /**
   * The type and the predicted end date of a deactivation only live on the patient historic,
   * so they are read back from there instead of from the patient himself.
   */
  private loadDeactivation(): void {
    this.deactivation = null;
    if (!this.patientUser || this.patientUser.patient?.active !== false) {
      return;
    }
    this.patientService
      .getPatientHistoric(this.patientUser.user?.caremateIdentifier, this.patientUser.patient?.managingOrganization?.reference)
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe({
        next: (historic: IPatientHistoric[]) => {
          // The patient is inactive, so the most recently written entry is the deactivation he is under.
          // `date` can be in the past (e.g. a backdated hospitalization), so `modified` is what
          // actually tells which entry was written last.
          const sortedHistoric = [...(historic ?? [])].sort((a, b) => moment(b.modified).diff(moment(a.modified)));
          const current = sortedHistoric[0];
          const subStatus = current ? PatientHelper.getSubStatusFromReason(current.newReason) : undefined;
          this.deactivation = current
            ? {
                permanent: current.permanent,
                predictedEndDate: current.predictedEndDate,
                date: current.date,
                // The reason could not be matched to a known sub status: it is the free text
                // typed for "other" instead.
                subStatus: subStatus ?? (current.newReason ? DISABLE_REASON.OTHER : undefined),
                reasonDetail: !subStatus ? current.newReason : undefined,
              }
            : null;
        },
        error: (err) => {
          // Reading the historic needs its own permission: without it we simply show no badge.
          FileLogger.error("PatientInfosComponent", "loadDeactivation", err, "none");
        },
      });
  }

  public disablePatient(patient: IPatientUser): void {
    const patients = [];
    patients.push(patient);
    this.dialog
      .open(PatientDisableDialogComponent, {
        data: { patients },
        disableClose: true,
      })
      .afterClosed()
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((deactivation: IPatientDeactivation) => {
        if (deactivation) {
          this.deactivation = { permanent: deactivation.permanent, predictedEndDate: deactivation.predictedEndDate };
        }
      });
  }

  /**
   * A patient under a temporary deactivation does not have to come back for it to be worth
   * touching: the practitioner is offered to change it instead of ending it.
   */
  public activatePatient(patient: IPatient): void {
    if (this.isTemporarilyDisabled && this.canUpdateDeactivation) {
      this.askReactivateOrUpdate(patient);
      return;
    }
    this.confirmActivation(patient);
  }

  private askReactivateOrUpdate(patient: IPatient): void {
    this.dialog
      .open(PatientReactivateDialogComponent, { disableClose: true })
      .afterClosed()
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((choice: REACTIVATE_CHOICE) => {
        if (choice === REACTIVATE_CHOICE.ACTIVATE) {
          this.activate(patient);
        } else if (choice === REACTIVATE_CHOICE.UPDATE_DEACTIVATION) {
          this.updateDeactivation();
        }
      });
  }

  private confirmActivation(patient: IPatient): void {
    this.translateService
      .get("page.patientlist.activatePatientConfirm")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((trans: string) => {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          data: { message: trans, type: ConfirmationDialogType.CONFIRM },
        });

        dialogRef.afterClosed().subscribe((yes) => {
          if (yes) {
            this.activate(patient);
          }
        });
      });
  }

  private activate(patient: IPatient): void {
    this.patientService.activate(patient).subscribe(
      () => {
        this.deactivation = null;
        this.sessionService.needRefreshPatientUser();
        this.sessionService.needRefreshPrescriptionsTile();
        const msg = this.translateService.instant("common.success");
        this.snackBar.open(msg, "ok", { duration: 3000 });
      },
      (err) => {
        FileLogger.error("PatientInfosComponent", "activatePatient", err);
        const msg = this.translateService.instant("common.updateFail");
        this.snackBar.open(msg, "ok", { duration: 3000 });
      }
    );
  }

  /** The dialog sends the change itself: what comes back is what the deactivation now looks like. */
  private updateDeactivation(): void {
    this.dialog
      .open(PatientDeactivationUpdateDialogComponent, {
        data: { patient: this.patientUser, deactivation: this.deactivation },
        disableClose: true,
      })
      .afterClosed()
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((update: IPatientDeactivationUpdate) => {
        if (update) {
          this.deactivation = {
            ...this.deactivation,
            subStatus: update.subStatus ?? this.deactivation?.subStatus,
            predictedEndDate: update.predictedEndDate ?? this.deactivation?.predictedEndDate,
          };
        }
      });
  }

  public getAuthCode(patient: IPatient): string | null {
    return this.patientService.computeAuthCode(patient);
  }

  public removeForEver(patientId: string): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message: this.translateService.instant("common.removeForEverAsk"),
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .subscribe((yes) => {
        if (yes) {
          this.patientService.removeForEver(patientId).subscribe(() => {
            const msg = this.translateService.instant("common.success");
            this.snackBar.open(msg, "ok", { duration: 3000 });
            this.goToPatients();
          });
        }
      });
  }

  private setupIsAdmin() {
    if (this.sessionService.isAdmin()) {
      this.isAdmin = true;
      this.userService
        .isAuthorized("patient", "DELETE")
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((isAuthorized) => {
          this.canDelete = isAuthorized;
        });
    } else {
      this.isAdmin = false;
      this.canDelete = false;
    }
  }

  private setupCanDeactivate() {
    this.userService
      .isAuthorized("deactivatePatient", "PUT")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isAuthorized) => {
        this.canDeactivate = isAuthorized;
      });
  }

  private setupCanUpdateDeactivation() {
    this.userService
      .isAuthorized("patient/deactivation/temporary/status", "PUT")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isAuthorized) => {
        this.canUpdateDeactivation = isAuthorized;
      });
  }

  private setupCanUpdate() {
    this.userService
      .isAuthorized("patient", "PUT")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isAuthorized) => {
        this.canUpdate = isAuthorized;
      });
  }

  public openPatientHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: {
          slides: this.helpData.patientPageHelp(this.canDelete, this.canDeactivate, this.canUpdate),
        },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  /**
   * Request server to reset patient password (only when user is fully registered)
   */
  public resetPatientPassword(patient: IPatientUser): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message: this.translateService.instant("page.patientlist.resetPasswordConfirm"),
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .subscribe((yes) => {
        if (yes) {
          this.authService.resetPassword(patient?.user.caremateIdentifier).subscribe({
            next: () => {
              this.patientUser.patient.noSms = false; // If the reset was successful, noSms is set to false
              this.snackBar.open(this.translateService.instant("common.success"), undefined, {
                duration: 3000,
              });
            },
            error: (error) => {
              FileLogger.error("PatientInfosComponent", "Error while trying to reset password", error, "none");
              this.snackBar.open(this.translateService.instant("api.errors.server-error"), "ok", { duration: 5000 });
            },
          });
        }
      });
  }

  public openHistoric(patient: IPatientUser): void {
    this.dialog.open(PatientHistoricDialogComponent, {
      data: { patient },
      disableClose: true,
    });
  }

  public showStatusChangeHistoric(patient: IPatientUser): void {
    const service = patient.patient.healthcareservice[0];
    const serviceIdentityVerifMethod = this.userService.allServices.find((s) => s.serviceRef === service?.reference)?.identityVerifMethods;
    this.dialog.open(IdentityStatusChangeHistoricComponent, {
      data: { identity: patient.patient.identity, serviceIdentityVerifMethod },
    });
  }

  /**
   * Request server to disable patient 2FA
   */
  public disablePatient2FA(patient: IPatientUser): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message: this.translateService.instant("page.patientlist.resetPassword") + " ?",
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (Tools.isNotDefined(result)) {
          // cancel
          return;
        }
        const reinitPwd = result ? true : false;
        this.authService.disablePatient2FA(patient?.user.caremateIdentifier, reinitPwd).subscribe({
          next: () => {
            this.snackBar.open(this.translateService.instant("common.success"), undefined, {
              duration: 3000,
            });
          },
          error: (error) => {
            FileLogger.error("PatientInfosComponent", "Error while trying to disable 2FA", error, "none");
            this.snackBar.open(this.translateService.instant("api.errors.server-error"), "ok", { duration: 5000 });
          },
        });
      });
  }
}
